/**
 * Keeps the field someone is typing in above the on-screen keyboard, on the
 * website (phones and tablets) and in the iOS app.
 *
 * In the app, the Keyboard plugin (resize: "native" in capacitor.config.json)
 * shrinks the web view when the keyboard opens and switches off WebKit's own
 * scroll-to-field, so a field in the lower part of the screen stayed hidden
 * behind the keyboard until the customer scrolled (the sign-up form's Email
 * and Password, for one). Phone browsers mostly reveal the field themselves,
 * but not reliably inside dialogs and scrolling panels.
 *
 * Once the keyboard has opened (the visual viewport shrinks, the app's
 * keyboardDidShow event fires, or shortly after focus), a text field that is
 * even partly hidden is scrolled to the middle of what's still visible.
 * Fields already in view don't move.
 */
import { isNativeApp } from '@/native/bootstrap';

/** Inputs that bring up a keyboard or picker; buttons and toggles don't. */
const NO_KEYBOARD_TYPES = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);
/** Time for the keyboard to finish opening when no resize event says so. */
const FOCUS_DELAY_MS = 400;
/** Time for layout to settle after the viewport resizes. */
const SETTLE_MS = 60;
/** How close to the visible edge still counts as hidden, in CSS pixels. */
const EDGE_MARGIN = 12;

export function isTextField(el) {
  if (!el || el.disabled || el.readOnly) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NO_KEYBOARD_TYPES.has(String(el.type || 'text').toLowerCase());
  return el.isContentEditable === true;
}

/** The part of the page the customer can see: above the keyboard, in page-viewport coordinates. */
export function visibleBand(win = window) {
  const vv = win.visualViewport;
  if (!vv) return { top: 0, bottom: win.innerHeight };
  return { top: vv.offsetTop, bottom: vv.offsetTop + vv.height };
}

export function isPartlyHidden(rect, band, margin = EDGE_MARGIN) {
  return rect.top < band.top + margin || rect.bottom > band.bottom - margin;
}

/**
 * Whether scrolling can bring the field into view. A field in a fixed panel
 * with no scrolling area of its own is pinned to the screen, and scrolling the
 * page would only move everything behind it.
 */
export function canScrollToField(el, win = window) {
  for (let node = el.parentElement; node && node !== el.ownerDocument.documentElement; node = node.parentElement) {
    const style = win.getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return true;
    if (style.position === 'fixed') return false;
  }
  return true;
}

/** Scroll the focused field into the visible area if any of it is hidden. True when it scrolled. */
export function revealFocusedField(doc = document, win = window) {
  const el = doc.activeElement;
  if (!isTextField(el) || !el.isConnected || typeof el.scrollIntoView !== 'function') return false;
  const rect = el.getBoundingClientRect();
  if (!rect.width && !rect.height) return false;
  if (!isPartlyHidden(rect, visibleBand(win))) return false;
  if (!canScrollToField(el, win)) return false;
  el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  return true;
}

/** Devices with an on-screen keyboard: the iOS app, and touch-first browsers. */
export function hasOnScreenKeyboard(win = window, native = isNativeApp) {
  if (native) return true;
  try {
    return Boolean(win.matchMedia?.('(pointer: coarse)').matches);
  } catch {
    return false;
  }
}

/** Start watching. Returns a function that stops. */
export function installKeyboardAvoidance({ win = window, doc = document, native = isNativeApp } = {}) {
  if (!hasOnScreenKeyboard(win, native)) return () => {};
  let timer = null;
  const revealSoon = (delay) => {
    win.clearTimeout(timer);
    timer = win.setTimeout(() => revealFocusedField(doc, win), delay);
  };
  const onFocusIn = (event) => {
    if (isTextField(event.target)) revealSoon(FOCUS_DELAY_MS);
  };
  const onKeyboardOpened = () => {
    if (isTextField(doc.activeElement)) revealSoon(SETTLE_MS);
  };

  doc.addEventListener('focusin', onFocusIn);
  win.visualViewport?.addEventListener('resize', onKeyboardOpened);
  // The iOS app's Keyboard plugin also announces the keyboard on window.
  win.addEventListener('keyboardDidShow', onKeyboardOpened);
  return () => {
    win.clearTimeout(timer);
    doc.removeEventListener('focusin', onFocusIn);
    win.visualViewport?.removeEventListener('resize', onKeyboardOpened);
    win.removeEventListener('keyboardDidShow', onKeyboardOpened);
  };
}
